import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { IconPicker, IconDisplay } from '@/components/ui/icon-picker';

interface Program {
  id: string;
  title: { ar: string; en: string };
  description: { ar: string; en: string };
  icon: string;
  image: string;
  order: number;
}

export function ProgramsSection() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [currentProgram, setCurrentProgram] = useState<Program | null>(null);
  const [formData, setFormData] = useState({
    title: { ar: '', en: '' },
    description: { ar: '', en: '' },
    icon: 'Users',
    image: '',
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const programsQuery = query(
        collection(db, 'landing_programs'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(programsQuery);

      const programsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];

      setPrograms(programsData);
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (program?: Program) => {
    if (program) {
      setCurrentProgram(program);
      setFormData({
        title: typeof program.title === 'string' ? { ar: program.title, en: '' } : program.title,
        description: typeof program.description === 'string' ? { ar: program.description, en: '' } : program.description,
        icon: program.icon,
        image: program.image,
      });
    } else {
      setCurrentProgram(null);
      setFormData({
        title: { ar: '', en: '' },
        description: { ar: '', en: '' },
        icon: 'Users',
        image: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentProgram(null);
    setFormData({
      title: { ar: '', en: '' },
      description: { ar: '', en: '' },
      icon: 'Users',
      image: '',
    });
  };

  const handleSave = async () => {
    try {
      if (currentProgram) {
        // Update existing program
        const programRef = doc(db, 'landing_programs', currentProgram.id);
        await updateDoc(programRef, {
          title: formData.title,
          description: formData.description,
          icon: formData.icon,
          image: formData.image,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Add new program
        await addDoc(collection(db, 'landing_programs'), {
          title: formData.title,
          description: formData.description,
          icon: formData.icon,
          image: formData.image,
          order: programs.length,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
      await fetchPrograms();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving program:', err);
    }
  };

  const handleDelete = async (programId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا البرنامج؟')) return;

    try {
      await deleteDoc(doc(db, 'landing_programs', programId));
      await fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newPrograms = [...programs];
    [newPrograms[index - 1], newPrograms[index]] = [newPrograms[index], newPrograms[index - 1]];

    await updateOrderInFirebase(newPrograms);
  };

  const handleMoveDown = async (index: number) => {
    if (index === programs.length - 1) return;

    const newPrograms = [...programs];
    [newPrograms[index], newPrograms[index + 1]] = [newPrograms[index + 1], newPrograms[index]];

    await updateOrderInFirebase(newPrograms);
  };

  const updateOrderInFirebase = async (orderedPrograms: Program[]) => {
    try {
      const updatePromises = orderedPrograms.map((program, index) =>
        updateDoc(doc(db, 'landing_programs', program.id), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );
      await Promise.all(updatePromises);
      await fetchPrograms();
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  if (loading) {
    return <Card className="p-6">جاري التحميل...</Card>;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة البرامج</h3>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 ml-2" />
          إضافة برنامج
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {programs.map((program, index) => (
          <Card key={program.id} className="overflow-hidden">
            <div className="relative h-32">
              <img
                src={program.image}
                alt={typeof program.title === 'string' ? program.title : program.title.ar}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <IconDisplay iconName={program.icon} className="w-5 h-5" />
                  {typeof program.title === 'string' ? program.title : program.title.ar}
                </h4>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 line-clamp-2 mb-4">{typeof program.description === 'string' ? program.description : program.description.ar}</p>
              <div className="flex gap-2 justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === programs.length - 1}
                >
                  ↓
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenModal(program)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(program.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {currentProgram ? 'تعديل برنامج' : 'إضافة برنامج جديد'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">عنوان البرنامج / Program Title</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="title-ar"
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    placeholder="مثال: مقياس نمط"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="title-en"
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    placeholder="e.g. Namat Scale"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">الوصف / Description</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="description-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Textarea
                    id="description-ar"
                    value={formData.description.ar}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })}
                    placeholder="وصف البرنامج..."
                    rows={4}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description-en" className="text-sm text-muted-foreground">English</Label>
                  <Textarea
                    id="description-en"
                    value={formData.description.en}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                    placeholder="Program description..."
                    rows={4}
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>الأيقونة</Label>
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsIconPickerOpen(true)}
                  className="w-full justify-start"
                >
                  <IconDisplay iconName={formData.icon} className="w-5 h-5 ml-2" />
                  {formData.icon}
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="image">رابط الصورة</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {formData.image && (
              <div className="relative h-48 rounded-lg overflow-hidden">
                <img
                  src={formData.image}
                  alt="معاينة"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                  <div className="text-white flex items-center gap-2">
                    <IconDisplay iconName={formData.icon} className="w-6 h-6" />
                    <span className="font-semibold">{formData.title.ar || 'معاينة'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              إلغاء
            </Button>
            <Button onClick={handleSave}>
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IconPicker
        open={isIconPickerOpen}
        value={formData.icon}
        onChange={(iconName) => setFormData({ ...formData, icon: iconName })}
        onClose={() => setIsIconPickerOpen(false)}
      />
    </Card>
  );
}

